import store from '@/lib/store'
import { getGuestDrafts, clearGuestDrafts } from '@/lib/services/guestDrafts'
import { getGuestId, rotateGuestId } from '@/lib/services/guestId'
import api from '@/lib/services/general.service'
// The 401 → login one-shot guard (busy-my-web#321). Only its RESET is used here — the redirect
// itself belongs to the interceptor, which is where a 401 actually arrives.
import { clearLoginRedirect, sessionStore } from '@/lib/authRedirectGuard'

// BFF Configuration - Duende BFF pattern with cookie-based authentication
const BFF_BASE_URL = process.env.VUE_APP_BFF_URL

/**
 * The three answers `probeSession` can give. Only SESSION_ENDED is a reason to
 * send the user to the identity provider — see #26. They live in lib/sessionState.js so the
 * interceptor's 401 handling can read them without importing this module (busy-my-web#377), and
 * are re-exported here so nothing that imports them from auth.service has to move.
 */
import { SESSION_ACTIVE, SESSION_ENDED, SESSION_UNKNOWN } from '@/lib/sessionState'
export { SESSION_ACTIVE, SESSION_ENDED, SESSION_UNKNOWN }

/**
 * How many consecutive "could not ask" answers the monitor tolerates before it
 * gives up and re-authenticates.
 *
 * Not zero, because a single blip must not sign anyone out; not unbounded,
 * because a BFF that answers 502 forever should eventually land the user
 * somewhere honest rather than in a silently dead session.
 */
export const UNKNOWN_TOLERANCE = 3

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

// Push any items a guest created before signing in (localStorage) up to the backend. Runs once at
// sign-in, BEFORE the fetch* calls below — those then replace the local copies with the real ones.
// Mirrors the normal logged-in create flow: each entity is created AND spawns its own ongoing (story).
async function syncGuestDrafts() {
  // 1. The guest's ongoings live SERVER-SIDE under the derived guest user id (X-Guest-Id) — one
  // claim call re-owns them all (children follow their OngoingId), then the id rotates so the
  // next signed-out session starts fresh.
  try {
    const guestId = getGuestId()
    if (guestId) {
      await api.insert('ongoing/claim-guest', { guestId })
      rotateGuestId()
      store.dispatch('ongoing/fetch', null, { root: true })
    }
  } catch (e) {
    console.error('Failed to claim guest ongoings:', e)
  }

  // 2. Entity drafts (tasks/notes/events) are still local — push them up. A draft that was created
  // INTO a guest story is already a step of a claimed ongoing, so it does not ALSO spawn one.
  const drafts = getGuestDrafts()
  const ACTION = { task: 'task/addTask', note: 'note/addNote', event: 'event/addEvent' }
  for (const d of drafts) {
    const action = ACTION[d.type]
    if (!action) continue
    try {
      await store.dispatch(action, { ...d.payload, silentToast: true }, { root: true })
      if (d.inStory) continue
      // Same as creating while logged in: the entity starts a new ongoing seeded from it.
      const title = d.payload.title || d.payload.summary || 'New'
      const story = stripHtml(d.payload.content || d.payload.description) || title
      await store.dispatch('ongoing/createFromEntity', { title, story, origin: d.type }, { root: true })
    } catch (e) {
      console.error('Failed to sync guest draft:', d.type, e)
    }
  }
  clearGuestDrafts()
}

let isRedirectingToLogin = false
// Vue app's base URL - for return redirects after authentication
// Always the origin the user is actually on: the app serves on both busy.me and my.busy.me, and
// a pinned env value (the old VUE_APP_BASE_URL) would bounce a busy.me visitor over to
// my.busy.me after login.
const APP_BASE_URL = window.location.origin

/**
 * Transform BFF user claims to match the expected user structure
 * BFF returns claims as an array: [{ type: "claim_type", value: "claim_value" }, ...]
 */
function transformBffClaims(claims) {
  if (!claims || !Array.isArray(claims)) {
    return null
  }

  const profile = {}
  claims.forEach(claim => {
    // Map common claim types to profile properties
    switch (claim.type) {
      case 'sub':
        profile.sub = claim.value
        break
      case 'name':
        profile.name = claim.value
        break
      case 'email':
        profile.email = claim.value
        break
      case 'role':
        profile.role = claim.value
        break
      case 'preferred_username':
        profile.preferred_username = claim.value
        break
      case 'subscription_plan':
        profile.subscription_plan = claim.value
        break
      case 'subscription_status':
        profile.subscription_status = claim.value
        break
      case 'subscription_period_end':
        profile.subscription_period_end = claim.value
        break
      default:
        // Store other claims directly
        profile[claim.type] = claim.value
    }
  })

  // Return structure compatible with existing store expectations
  return {
    profile,
    // Tokens are managed server-side with BFF, not available client-side
    access_token: null,
    id_token: null
  }
}

export const auth = {
  /**
   * Authenticate user - check if logged in via BFF, redirect to login if not
   */
  async authenticate(returnPath) {
    const user = await auth.getUser()
    if (user) {
      store.dispatch('user/fetchUser', user)
    } else {
      store.dispatch('user/fetchUser', null)
      auth.signIn(returnPath)
    }
  },

  /**
   * Load user from BFF and dispatch to store
   */
  async loadUserToStore() {
    try {
      const user = await auth.getUser()
      if (user) {
        await store.dispatch('user/fetchUser', user)
        // THE HOME TIMELINE FIRST (busy-my-web#438, Saffet: "it takes 15 seconds to see the
        // timeline"). Everything after this point is the app's whole working set — every task,
        // every contact, the notes, the drafts, the spam list — behind a guest-draft sync that
        // AWAITS a write of its own (`ongoing/claim-guest` runs on every boot, because a visitor
        // always has a guest id). The day's feed, which is the one list the user is looking at on
        // /home, was issued after all of that and arrived last. It goes first now, unawaited: the
        // store keeps a single request in flight, so the side panel's own mount-time dispatch joins
        // THIS read rather than making a second one, whichever of the two happens first.
        // Ordering cost, accepted: a guest who had just created something sees it join the feed on
        // the next read (the write-refresh plugin, lib/globalTimelineRefresh) rather than in this
        // first one. A sign-in with guest work on it is a once-per-account event; a slow home page
        // is every refresh.
        store.dispatch('globalTimeline/fetch', null, { root: true })
        // Now authenticated — push guest drafts to the backend before the fetches replace the lists.
        await syncGuestDrafts()
        // Google connection status gates email compose + attachments app-wide (not just on Inbox).
        store.dispatch('integrations/fetchStatus')
        store.dispatch('timeline/fetchTimelineCount', null, { root: true })
        store.dispatch('spamEmail/fetchEmails', null, { root: true })
        store.dispatch('draftEmail/fetchEmails', null, { root: true })
        store.dispatch('note/fetchNotesAndCounts', null, { root: true })
        store.dispatch('contacts/fetchAllContacts', null, { root: true })
        store.dispatch('task/fetchTasks', { page: 1, pageSize: 10000, order: 1, search: '' }, { root: true })
        store.dispatch('event/fetchEventsAndCounts', null, { root: true })
        store.dispatch('timeline/startAutoRefresh', null, { root: true })
        // No Pro-only full-mailbox sync to resume any more (Pouria, 2026-09-29: Pro only lifts the
        // AI limit; every mailbox is synced whole on connect), so boot is the same for every plan.
        startSessionMonitoring()
      }
    } catch (err) {
      console.error('Error loading user to store:', err)
    }
  },

  /**
   * Get current user from BFF /bff/user endpoint
   * Returns user claims if authenticated, null if not
   */
  async getUser() {
    const result = await auth.probeSession()
    return result.state === SESSION_ACTIVE ? result.user : null
  },

  /**
   * Ask the BFF who the user is, distinguishing the three answers that matter.
   *
   * `getUser` flattens this to "a user or nothing", which is all its callers
   * want. The session monitor needs more: it used to treat *every* failure as
   * "signed out" and redirect to the IdP, so one 502, one dropped connection,
   * or a laptop waking from sleep signed the user out mid-session (#26). Only
   * a 401 means that; anything else means we could not ask.
   *
   * @returns {Promise<{state: string, user?: object}>} one of
   *   SESSION_ACTIVE (with `user`), SESSION_ENDED, or SESSION_UNKNOWN.
   */
  async probeSession() {
    try {
      const response = await fetch(`${BFF_BASE_URL}/bff/user`, {
        credentials: 'include', // Include cookies for authentication
        headers: {
          'X-CSRF': '1' // Anti-forgery header required by BFF
        }
      })

      // The only answer that actually means "you are signed out".
      if (response.status === 401) {
        return { state: SESSION_ENDED }
      }

      // 500/502/503 and friends: the BFF is unwell, not the session.
      if (!response.ok) {
        console.warn(`BFF user endpoint returned ${response.status} — session state unknown`)
        return { state: SESSION_UNKNOWN }
      }

      const user = transformBffClaims(await response.json())
      // A 200 whose body does not parse into claims is a server fault, not a
      // signed-out user — same reasoning as the 5xx branch above.
      if (!user) {
        console.warn('BFF user endpoint returned an unreadable body — session state unknown')
        return { state: SESSION_UNKNOWN }
      }
      // A session that answers with real claims is the proof that whatever sent the user to log in
      // is over, so the one-shot 401 guard is released here (busy-my-web#321): without this, a user
      // who has just signed in normally would be one 401 away from being refused their single
      // redirect and shown a signed-out page instead.
      clearLoginRedirect(sessionStore())
      return { state: SESSION_ACTIVE, user }
    } catch (err) {
      // Network rejection, offline, CORS, aborted — we never got an answer.
      console.warn('Could not reach the BFF user endpoint — session state unknown:', err)
      return { state: SESSION_UNKNOWN }
    }
  },

  /**
   * Redirect to BFF login endpoint
   */
  signIn(returnPath) {
    if (isRedirectingToLogin) return
    isRedirectingToLogin = true
    const loginUrl = new URL(`${BFF_BASE_URL}/bff/login`)
    // Use full URL to Vue app - BFF must be configured to allow this origin
    if (returnPath) {
      loginUrl.searchParams.set('returnUrl', `${APP_BASE_URL}${returnPath}`)
    } else {
      loginUrl.searchParams.set('returnUrl', `${APP_BASE_URL}${window.location.pathname}${window.location.search}`)
    }
    window.location.href = loginUrl.toString()
  },

  /**
   * Check if session is still valid (BFF handles token renewal automatically)
   * Returns true if valid, false if expired
   */
  async checkSession() {
    // BFF automatically refreshes tokens server-side when needed
    // We just check if the session is still alive
    return (await auth.probeSession()).state === SESSION_ACTIVE
  },

  /**
   * Silent renewal (busy-my-web#377).
   *
   * The browser holds no token, so there is nothing for it to refresh: the BFF renews the access
   * token server-side, on the first gated request that needs one, and answers 401 only when the
   * identity provider refuses the refresh. What the browser CAN do silently is confirm the session
   * and bring the store's user back in line with it — which is what this does, with no iframe and
   * no page load. A caller that gets `false` has a session that is genuinely over (or a BFF that
   * could not be asked); what to do about it is the caller's decision, never this function's —
   * the 401 handler in the interceptor and the session monitor each have their own rule.
   *
   * @returns {Promise<boolean>} true when the session is alive and the store's user is current.
   */
  async silentRenew() {
    const result = await auth.probeSession()
    if (result.state !== SESSION_ACTIVE) return false
    await store.dispatch('user/fetchUser', result.user)
    return true
  },

  /**
   * Handle silent login callback (deprecated with BFF)
   */
  signinSilentCallback() {
    // With BFF, this is not needed - kept for compatibility
    console.log('Silent callback - no action needed with BFF')
  },

  /**
   * Sign out - redirects to BFF logout which then redirects to IDS
   * This performs a full OIDC logout from Identity Server
   */
  signOut() {
    // Clear local storage before redirect
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('oidc.') || key.startsWith('bff.'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))

    // Clear session storage
    sessionStorage.clear()

    // Clear user from store
    store.dispatch('user/fetchUser', null)

    // Stop session monitoring
    stopSessionMonitoring()

    // Redirect to BFF logout endpoint
    // BFF will redirect to IDS for logout, then IDS will redirect back to returnUrl
    const logoutUrl = new URL(`${BFF_BASE_URL}/bff/logout`)
    logoutUrl.searchParams.set('returnUrl', APP_BASE_URL)

    // This redirect is necessary for proper OIDC logout from Identity Server
    window.location.href = logoutUrl.toString()
  },

  /**
   * Get the user's role from BFF
   */
  async getRole() {
    try {
      const user = await auth.getUser()
      if (!user) {
        auth.signIn()
        return null
      }
      return user.profile?.role
    } catch (err) {
      console.error('Error getting role:', err)
      return null
    }
  }
}

// Set up periodic session check
// BFF automatically refreshes tokens server-side, we just monitor session health
let sessionCheckInterval = null

/** Consecutive SESSION_UNKNOWN answers; reset by any definite answer. */
let unknownStreak = 0

async function handleVisibilityChange() {
  if (document.visibilityState !== 'visible') return
  // Re-focusing a tab is the most common moment to be offline for a second
  // (a laptop just woke up), which is exactly when the old check redirected.
  await monitorSessionOnce()
}

export function startSessionMonitoring() {
  if (sessionCheckInterval) return

  sessionCheckInterval = setInterval(monitorSessionOnce, 5 * 60 * 1000) // Check every 5 minutes

  document.addEventListener('visibilitychange', handleVisibilityChange)
}

/**
 * One pass of the session monitor.
 *
 * Redirects to the IdP only when the BFF actually says the session is over.
 * A run of "could not ask" answers is tolerated up to [UNKNOWN_TOLERANCE] —
 * a flaky connection, a sleeping laptop or a restarting BFF must not throw the
 * user out of what they were doing (#26).
 */
export async function monitorSessionOnce() {
  const { state } = await auth.probeSession()

  if (state === SESSION_ACTIVE) {
    unknownStreak = 0
    return
  }

  if (state === SESSION_ENDED) {
    unknownStreak = 0
    auth.signIn()
    return
  }

  unknownStreak += 1
  if (unknownStreak >= UNKNOWN_TOLERANCE) {
    console.warn(`Session state unknown ${unknownStreak} times running — re-authenticating`)
    unknownStreak = 0
    auth.signIn()
  }
}

export function stopSessionMonitoring() {
  unknownStreak = 0
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval)
    sessionCheckInterval = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

